import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Package } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { getSession } from "@/services/sessionStorage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Header from "@/components/shared/Header";
import Footer from "@/components/shared/Footer";

export default function TrackOrderLookup() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [orderNumber, setOrderNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isAr = language === "ar";

  const handleTrack = async () => {
    const raw = orderNumber.trim();
    if (!raw) return;

    setLoading(true);
    setError("");

    try {
      const session = getSession();
      const customerId = session.customer?.data?.id as string || "";

      if (!customerId) {
        setError(isAr ? "يرجى تسجيل الدخول أولاً لتتبع طلبك." : "Please log in first to track your order.");
        setLoading(false);
        return;
      }

      const withHash = raw.startsWith("#") ? raw : `#${raw}`;
      const withoutHash = raw.startsWith("#") ? raw.slice(1) : raw;

      const { data: order } = await supabase
        .from("orders")
        .select("id, order_number")
        .eq("customer_id", customerId)
        .or(`order_number.eq.${withHash},order_number.eq.${withoutHash}`)
        .limit(1)
        .maybeSingle();

      if (order) {
        const orderIdentifier = order.order_number
          ? order.order_number.replace(/^#/, "")
          : order.id;
        navigate(`/track/${orderIdentifier}`);
      } else {
        setError(isAr ? "لم يتم العثور على الطلب. تأكد من رقم الطلب." : "Order not found. Please check the order number.");
      }
    } catch (err) {
      console.error("Track order error:", err);
      setError(isAr ? "حدث خطأ. حاول مرة أخرى." : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" dir={isAr ? "rtl" : "ltr"}>
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md space-y-8 text-center">
          <div className="flex justify-center">
            <div className="rounded-full bg-primary/10 p-4">
              <Package className="h-10 w-10 text-primary" />
            </div>
          </div>

          <div className="space-y-2">
            <h1 className="text-3xl font-heading font-bold text-foreground">
              {isAr ? "تتبع طلبك" : "Track Your Order"}
            </h1>
            <p className="text-muted-foreground">
              {isAr ? "أدخل رقم الطلب لمتابعة حالته" : "Enter your order number to check its status"}
            </p>
          </div>

          <div className="space-y-4">
            <div className="relative">
              <Input
                placeholder={isAr ? "مثال: 1042" : "e.g. 1042"}
                value={orderNumber}
                onChange={(e) => { setOrderNumber(e.target.value); setError(""); }}
                onKeyDown={(e) => e.key === "Enter" && handleTrack()}
                className="h-12 text-center text-lg"
              />
            </div>

            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}

            <Button
              onClick={handleTrack}
              disabled={loading || !orderNumber.trim()}
              className="w-full h-12 text-base gap-2"
            >
              <Search className="h-5 w-5" />
              {loading
                ? (isAr ? "جاري البحث..." : "Searching...")
                : (isAr ? "تتبع الطلب" : "Track Order")}
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
